/**
 * useTasks Hook
 *
 * Hook for managing tasks related to a contact/conversation in inbox-v3.
 * Uses existing RTK Query endpoints from cellcast.v2.
 */

import {useCallback, useMemo} from 'react'
import {
  useGetTasksByContactIdQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useCompleteTaskMutation,
  useDeleteTaskMutation,
} from '../../../../../services/cellcast.v2'
import {
  TaskItem,
  CreateTaskPayload,
  UpdateTaskPayload,
} from '../../../../../services/cellcast.v2.types'

export interface UseTasksOptions {
  /** Contact ID to filter tasks by (contact group map ID) */
  contactId?: string
  /** Whether to enable the query */
  enabled?: boolean
  /** Page number for pagination */
  page?: number
  /** Limit for pagination */
  limit?: number
}

export interface UseTasksReturn {
  /** List of tasks for the contact */
  tasks: TaskItem[]
  /** Whether tasks are loading */
  isLoading: boolean
  /** Whether tasks are being fetched */
  isFetching: boolean
  /** Error if any */
  error: string | null
  /** Create a new task */
  createTask: (task: CreateTaskPayload) => Promise<TaskItem | null>
  /** Update an existing task */
  updateTask: (id: string, data: UpdateTaskPayload) => Promise<TaskItem | null>
  /** Mark a task as complete */
  completeTask: (id: string) => Promise<boolean>
  /** Delete a task */
  deleteTask: (id: string) => Promise<boolean>
  /** Refetch tasks */
  refetch: () => void
  /** Whether a task operation is in progress */
  isSubmitting: boolean
}

export function useTasks(options: UseTasksOptions = {}): UseTasksReturn {
  const {contactId, enabled = true, page = 1, limit = 10} = options

  // Fetch tasks by contact ID
  const {
    data: tasksResponse,
    isLoading,
    isFetching,
    error: queryError,
    refetch,
  } = useGetTasksByContactIdQuery(
    {contactId: contactId || '', page, limit},
    {
      skip: !enabled || !contactId,
      refetchOnMountOrArgChange: true,
    }
  )

  // Mutations
  const [createTaskMutation, {isLoading: isCreating}] = useCreateTaskMutation()
  const [updateTaskMutation, {isLoading: isUpdating}] = useUpdateTaskMutation()
  const [completeTaskMutation, {isLoading: isCompleting}] = useCompleteTaskMutation()
  const [deleteTaskMutation, {isLoading: isDeleting}] = useDeleteTaskMutation()

  // Extract tasks from response and normalize the id field
  // API may return _id instead of id, so we ensure each task has a valid id
  const tasks = useMemo(() => {
    if (!tasksResponse?.data?.items) return []
    return tasksResponse.data.items.map((task, idx) => ({
      ...task,
      // Ensure id is always set - prefer id, fallback to _id
      id: task.id || task._id || `temp-${idx}`,
    }))
  }, [tasksResponse])

  // Error handling
  const error = useMemo(() => {
    if (queryError) {
      return 'Failed to load tasks'
    }
    return null
  }, [queryError])

  // Create task
  const createTask = useCallback(
    async (taskData: CreateTaskPayload): Promise<TaskItem | null> => {
      try {
        // Merge current contactId with any additional contacts from form
        const formContacts = taskData.contacts || []
         
        const allContacts = contactId ? Array.from(new Set([contactId, ...formContacts])) : formContacts

        const payload: CreateTaskPayload = {
          ...taskData,
          contacts: allContacts,
          leads: taskData.leads || [],
        }
        const result = await createTaskMutation(payload).unwrap()
        // API may return 'success' or 'status' for success indicator
        const isSuccess = (result as any).success || (result as any).status
        if (isSuccess && result.data) {
          refetch()
          return result.data
        }
        return null
      } catch (err) {
        console.error('Failed to create task:', err)
        return null
      }
    },
    [createTaskMutation, contactId, refetch]
  )

  // Update task
  const updateTask = useCallback(
    async (id: string, data: UpdateTaskPayload): Promise<TaskItem | null> => {
      try {
        const result = await updateTaskMutation({id, data}).unwrap()
        // API may return 'success' or 'status' for success indicator
        const isSuccess = (result as any).success || (result as any).status
        if (isSuccess && result.data) {
          refetch()
          return result.data
        }
        return null
      } catch (err) {
        console.error('Failed to update task:', err)
        return null
      }
    },
    [updateTaskMutation, refetch]
  )

  // Complete task
  const completeTask = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const result = await completeTaskMutation(id).unwrap()
        // API may return 'success' or 'status' for success indicator
        const isSuccess = (result as any).success || (result as any).status
        if (isSuccess) {
          refetch()
          return true
        }
        return false
      } catch (err) {
        console.error('Failed to complete task:', err)
        return false
      }
    },
    [completeTaskMutation, refetch]
  )

  // Delete task
  const deleteTask = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const result = await deleteTaskMutation(id).unwrap()
        // API may return 'success' or 'status' for success indicator
        const isSuccess = (result as any).success || (result as any).status
        if (isSuccess) {
          refetch()
          return true
        }
        return false
      } catch (err) {
        console.error('Failed to delete task:', err)
        return false
      }
    },
    [deleteTaskMutation, refetch]
  )

  const isSubmitting = isCreating || isUpdating || isCompleting || isDeleting

  return {
    tasks,
    isLoading,
    isFetching,
    error,
    createTask,
    updateTask,
    completeTask,
    deleteTask,
    refetch,
    isSubmitting,
  }
}

export default useTasks
